import { AfterViewInit, Component, OnInit } from "@angular/core";
import { Blackbox } from "@models/blackbox";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { BlackboxService } from "@services/entity/blackbox.service";
import { ToastService } from "@services/toast/toast.service";
import { ChargingStationDto } from "@dto/chargingStationDto";
import { BidiConfiguration } from "@models/bidiConfiguration";
import { BidiConfigurationService } from "@services/entity/bidi-configuration.service";
import { BidiConfigurationDto } from "@dto/bidiConfigurationDto";
import { ChargingStationsService } from "@services/entity/charging-stations.service";
import { BidiSelectChargingStationComponent } from "./bidi-select-charging-station/bidi-select-charging-station.component";
import { FormBuilder, FormGroup } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";

@Component({
  selector: "app-bidi-charging",
  templateUrl: "./bidi-charging.component.html",
  styleUrls: ["./bidi-charging.component.scss"],
})
export class BidiChargingComponent implements OnInit, AfterViewInit {
  selectedBidiModeId: number = 1;
  currentValues = { from: 30, to: 70 };
  targetPowerSliderValue: number = 0;
  saveDisabled: boolean = true;
  customerId: number | null = null;

  // Summe der ausgewählten Ladestationen
  maxBidiChargePowerSum: number = 0;
  minBidiChargePowerSum: number = 0;
  maxBidiDischargePowerSum: number = 0;
  minBidiDischargePowerSum: number = 0;

  chargingStations: ChargingStationDto[] = [];
  selectedChargingStations: ChargingStationDto[] = [];

  blackboxes: Blackbox[] = []; // Blackboxes (Gateways) für die Anzeige
  selectedBlackbox: Blackbox | null = null; // Ausgewählte Blackbox

  constructor(
    private blackboxService: BlackboxService,
    private chargingStationsService: ChargingStationsService,
    private toastService: ToastService,
    private bidiConfigurationService: BidiConfigurationService,
    private route: ActivatedRoute,
    public dialog: NgbModal
  ) {
  }


  async ngOnInit(): Promise<void> {
    this.setCustomerId();

    this.blackboxes = await this.getBlackboxes();

    if (!this.blackboxes.length) {
      this.blackboxes = [
        {
          id: 1,
          serialNumber: "1234567890",
        } as Blackbox,
      ];
    }

    this.setPowerMinMaxAndSilder();
  }

  setCustomerId() {
    const id = this.route.parent.snapshot.params.key;
    if (id && !isNaN(Number(id))) {
      this.customerId = Number(id);
    } else {
      this.customerId = null;
    }
  }

  ngAfterViewInit(): void {
  }

  async getBlackboxes(): Promise<Blackbox[] | null> {
    try {
      let data: Blackbox[] | null;

      if (!this.customerId) {
        // Wenn customerId nicht gesetzt ist
        data = await this.blackboxService.getAllByCurrentCustomer().toPromise();
      } else {
        // Wenn customerId gesetzt ist
        data = await this.blackboxService.getByCustomerId(this.customerId).toPromise();
      }

      return data;
    } catch (err) {
      console.error("Fehler beim Laden der Gateways:", err);
      this.toastService.error("Fehler beim Laden der Gateways", 5000, true);
      return null;
    }
  }


  setPowerMinMaxAndSilder() {
    this.minBidiChargePowerSum = this.roundDownToHalf(
      this.chargingStations
        .map(x => x.profile.minBidiChargePower)
        .reduce((sum, value) => sum + value, 0) / 1000
    );

    this.maxBidiChargePowerSum = this.roundDownToHalf(
      this.chargingStations
        .map(x => x.profile.maxBidiChargePower)
        .reduce((sum, value) => sum + value, 0) / 1000
    );

    this.minBidiDischargePowerSum = this.roundDownToHalf(
      -Math.abs(
        this.chargingStations
          .map(x => x.profile.minBidiDischargePower)
          .reduce((sum, value) => sum + value, 0) / 1000
      )
    );

    this.maxBidiDischargePowerSum = this.roundDownToHalf(
      -Math.abs(
        this.chargingStations
          .map(x => x.profile.maxBidiDischargePower)
          .reduce((sum, value) => sum + value, 0) / 1000
      )
    );


    if (this.targetPowerSliderValue > this.maxBidiChargePowerSum)
      this.targetPowerSliderValue = this.maxBidiChargePowerSum;
    else if (this.targetPowerSliderValue < this.maxBidiDischargePowerSum)
      this.targetPowerSliderValue = this.maxBidiDischargePowerSum;
  }
  private roundDownToHalf(value: number): number {
    return Math.floor(value * 2) / 2;
  }

  selectBlackbox(blackbox: Blackbox): void {
    this.selectedBlackbox = blackbox;
  }

  onSliderValueChange(event: { from: number; to: number }) {
    this.currentValues = event;
  }

  // NOTE: Load charging stations inside this component to feed ng-select
  openScsDialog(): void {
    const dialogRef = this.dialog.open(BidiSelectChargingStationComponent, {
      size: "lg",
      centered: true,
      backdrop: "static"
    });
    dialogRef.componentInstance.preSelectedStations = this.chargingStations;
    dialogRef.componentInstance.customerId = this.customerId;

    dialogRef.result
      .then((selectedStations: ChargingStationDto[]) => {
        console.log("dialogRef.result", selectedStations);
        if (selectedStations) {
          this.chargingStations = selectedStations;
          this.setPowerMinMaxAndSilder();
        }
      })
      .catch((error) => { });
  }


  async loadSetting(gatewayId: number) {
    let bidiConfigDto;
    try {
      bidiConfigDto = await this.bidiConfigurationService
        .getBidiConfigurationDtoByGatewayId(gatewayId)
        .toPromise();
    } catch (error) {
      console.error('Fehler beim Abrufen der Bidi-Konfiguration:', error);
      // Fehlerbehandlung oder Rückgabe bei Fehlschlag
      this.saveDisabled = true;
      return;
    }

    this.chargingStations = bidiConfigDto.chargingStationDtos;
    this.setPowerMinMaxAndSilder();
    this.selectedBidiModeId = Number(bidiConfigDto.bidiModeId);
    this.targetPowerSliderValue = bidiConfigDto.targetPowerInWatt / 1000;
    this.currentValues = { from: bidiConfigDto.minSoc, to: bidiConfigDto.maxSoc };

    this.saveDisabled = false;
  }


  async saveSettings() {
    let bidiConfigurationDto: BidiConfigurationDto = new BidiConfigurationDto();

    bidiConfigurationDto.gatewayId = this.selectedBlackbox!.id;
    bidiConfigurationDto.bidiModeId = this.selectedBidiModeId.toString();
    bidiConfigurationDto.targetPowerInWatt = this.targetPowerSliderValue * 1000;
    bidiConfigurationDto.minSoc = this.currentValues.from;
    bidiConfigurationDto.maxSoc = this.currentValues.to;
    bidiConfigurationDto.chargingStationDtos = this.chargingStations;


    this.toastService.loading("Wird gespeichert...", true);
    try {
      this.saveDisabled = true;
      await this.bidiConfigurationService
        .createUpdateBidiConfiguration(bidiConfigurationDto)
        .toPromise();
      this.toastService.success("Bidi-Konfiguration erfolgreich gespeichert.", 4500, true);
      this.saveDisabled = false;
    } catch (error) {
      this.toastService.error("Fehler beim Speichern der Bidi-Konfiguration", 6500, true);
      this.saveDisabled = false;
    }
  }

  onBlackboxSelected(event: any): void {
    if (Array.isArray(event)) {
      this.selectedBlackbox = event[0];
    } else {
      this.selectedBlackbox = event;
    }

    this.loadSetting(this.selectedBlackbox.id);
  }



}
